import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { ColorAliases, QuestionnaireTypes } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ButtonComponent, LoaderModule, ConfirmationDialogComponent as MKConfirmationDialogComponent } from '@mds/ui';
import { ProductModel } from '@pages/information-requests/request/pcf-questionnaire/pcf-questionnaire.model';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { HostnameService } from '@services/hostname.service';
import { MakersiteService } from '@services/makersite.service';
import { MenuService } from '@services/menu.service';
import { QuestionnairesValidatorService } from '@services/questionnaires-validator.service';
import { ToastrService } from '@services/toaster.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { GridApi } from 'ag-grid-community';
import _ from 'lodash';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { catchError, finalize, Observable, of, throwError } from 'rxjs';
import { InformationRequestService } from '../information-request.service';
import { CbamQuestionnaireComponent } from './cbam-questionnaire/cbam-questionnaire.component';
import { ComplianceQuestionnaireComponent } from './compliance-questionnaire/compliance-questionnaire.component';
import { InfoRequestTilesComponent } from './info-request-tiles/info-request-tiles.component';
import { PcfQuestionnaireComponent } from './pcf-questionnaire/pcf-questionnaire.component';
import { QuestionTilesComponent } from './question-tile/question-tile.component';
import { ScipQuestionnaireComponent } from './scip-questionnaire/scip-questionnaire.component';
@Component({
  selector: 'app-request',
  standalone: true,
  imports: [
    ButtonComponent,
    LoaderModule,
    MdsIconsModule,
    QuestionTilesComponent,
    PcfQuestionnaireComponent,
    InfoRequestTilesComponent,
    CbamQuestionnaireComponent,
    ComplianceQuestionnaireComponent,
    PerfectScrollbarModule,
    ScipQuestionnaireComponent,
    RouterModule
  ],
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.scss'],
  providers: [DatePipe, QuestionnairesValidatorService, InformationRequestService]
})
export class RequestComponent implements OnInit, OnDestroy {
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private informationRequestService = inject(InformationRequestService);

  @ViewChild('errorDialogBody')
  errorDialogBody!: TemplateRef<any>;
  @ViewChild('errorDialogActions')
  errorDialogActions!: TemplateRef<any>;

  public errorDialogRef: MatDialogRef<MKConfirmationDialogComponent> = null;

  rid: string;
  response: any;
  ngxEventBus = [];
  discussionData: any;
  validCheck: any = {};
  questionnaireTypes = QuestionnaireTypes;
  selectedTab = 0;
  isLoading = true;
  userInfo = JSON.parse(localStorage.getItem('user_info'));
  isUserExternalSupplier = this.menuService.isExternalSupplier();
  errorMessages: { message: string; data: string | null }[] = [];
  allTabsValid: Map<string, boolean> = new Map();

  canDeactivate(): Observable<boolean> {
    if (document.querySelector('#inlineFormSchema input.ng-touched.ng-valid')) {
      return this.discardChangesDialogMsg();
    }
    return of(true);
  }

  discardChangesDialogMsg() {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          header: 'Warning',
          text: 'It looks like you have been editing something. Please save; otherwise, your changes will be lost.',
          cancel_text: 'Cancel',
          delete_text: 'Continue'
        }
      })
      .afterClosed();
  }

  actionValues: any;
  validationFunctionRef = null;

  threads = [];
  threadApiCompleted = false;
  gridApi: GridApi;

  constructor(
    private makersiteService: MakersiteService,
    private eventDelegatorService: EventDelegatorService,
    private hostnameService: HostnameService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private globalDataService: GlobalDataService,
    private route: ActivatedRoute,
    private questionnairesValidator: QuestionnairesValidatorService,
    private router: Router
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit(): void {
    this.rid = this.route.snapshot.paramMap.get('rid');
    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-request', (ev) => {
        if (ev.type === 'toolbar-reload-event') {
          this.init();
        } else if (ev.type === 'reload-card-section') {
          this.reloadSection(ev);
        } else if (ev.type === 'mega-action-event') {
          this.onMenuEvents(ev.action);
        }
      })
    );

    this.ngxEventBus.push(
      this.eventDelegatorService.on('submit-validation', (data) => {
        if (!this.response.request.viewData.actions?.submit && !this.response.actions?.submit) return;
        this.validCheck[data.data.name] = data.data.valid;
      })
    );
    this.init();
    this.setMenu();
  }

  setMenu() {
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) return;
      this.init();
    });
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) return;
      this.onMenuEvents(value);
    });
    if (!this.rid) return;
    this.makersiteService.getTileCards(this.rid).subscribe((res: ProductModel[]) => {
      const request = res[0];
      const entityIcons = [{ key: getEntityTypeIcon(request.stage) }];
      const currentMenus = this.menuService.menuConfigSubject.getValue();
      currentMenus.activeApp.menuTrigger = this.menuService.isExternalSupplier() ? '' : 'selectApps';
      this.menuService.setMenuConfig({
        ...currentMenus,
        activeEntity: {
          mainIcon: {
            icon: MdsIcon.COMPOSER_REQUEST
          },
          rid: this.rid,
          name: request.title,
          icons: entityIcons,
          subtitle: request.card_type_label
        }
      });
    });
  }

  ngOnDestroy() {
    // unsusbscribe all subscribers when component is destroyed
    this.ngxEventBus.forEach((e) => {
      if (e) e.unsubscribe();
    });
  }

  init() {
    this.discussionData = {
      tab: 'request',
      composerRid: this.rid
    };
    this.isLoading = true;
    this.threadApiCompleted = false;
    this.makersiteService.getSupplierRequest(this.rid, true).subscribe(
      (res: any) => {
        if (res.info?.help_text) {
          const pattern = /<[A-Za-z0-9_]+>/g;
          const matches = res.info.help_text.match(pattern);

          if (matches?.length) {
            matches.forEach((match) => {
              switch (match) {
                case '<RSL_NAME>':
                  res.info.help_text = res.info.help_text.replaceAll('<RSL_NAME>', res.additional_data.rsls[0].name);
                  break;
                case '<PRODUCT_COUNT>':
                  res.info.help_text = res.info.help_text.replaceAll('<PRODUCT_COUNT>', res.tabs[0].lines.length);
                  break;
                default:
                  break;
              }
            });
          }
        }

        res.requestRid = this.rid;

        this.response = _.cloneDeep(res);
        this.isLoading = false;
        this.selectedTab = this.response?.selected_tab_key === 'product_info' ? 1 : 0;
        const actions = res?.request?.viewData?.actions ?? res?.actions;
        if (actions) {
          const { plugin_id } = this.response;
          const menuActions = [];
          const isCompliance = plugin_id === QuestionnaireTypes.COMPLIANCE_QUESTIONNAIRE;
          if (actions.close) {
            actions.close = !isCompliance;
            actions.reopen = isCompliance ? false : actions.reopen;
            if (isCompliance) {
              menuActions.push({ close: { visible: false, label: 'Complete Review' } });
            }
          }
          this.actionValues = actions;
          Object.entries(actions).forEach(([action, value]) => {
            menuActions.push({ [action]: { visible: value } });
          });
          if (isCompliance) {
            menuActions.push({ back: { visible: true } });
          }
          this.actionValues = actions;
          this.menuService.updateMenuItems(menuActions);
        }
        if (
          [QuestionnaireTypes.COMPLIANCE_QUESTIONNAIRE, QuestionnaireTypes.SCIP_QUESTIONNAIRE].includes(
            this.response.plugin_id
          )
        ) {
          const payload = { questionnaire_id: this.rid };
          this.makersiteService
            .getQuestionaireThread(payload)
            .pipe(
              catchError(() => {
                this.threads = [];
                return of(null);
              }),
              finalize(() => {
                this.threadApiCompleted = true;
              })
            )
            .subscribe((threadResp: any) => {
              if (threadResp) {
                this.threads = threadResp.threads;
              }
            });
        }
      },
      () => {
        this.isLoading = false;
        this.redirect('information-requests');
      }
    );
  }

  redirect(url) {
    this.eventDelegatorService.emiter('custom-ngx-event', {
      url,
      type: 'window-open-event',
      controller: window.location.pathname
    });
  }

  formValueChanged(formValid: boolean, tab: any) {
    this.allTabsValid.set(tab.key, formValid);
    if (!this.actionValues?.submit) return;
    if (!this.validationFunctionRef) {
      this.validationFunctionRef = this.setFormValid();
    }
    this.validationFunctionRef();
  }

  /**
   * Enable and disable submit button based on all forms validations
   * Only Last function call will be entertained.
   * @returns function
   */
  setFormValid() {
    let timeoutId;
    return () => {
      // Clear the previous timeout
      clearTimeout(timeoutId);

      // Set a new timeout for the last call
      timeoutId = setTimeout(() => {
        const allFormsValid = Array.from(this.allTabsValid.values()).every((value) => value === true);

        this.menuService.updateMenuItem('submit', { visible: allFormsValid });
      }, 300);
    };
  }

  reloadSection(ev) {
    if (ev.isQuestions) return;
    this.makersiteService.getSupplierRequest(this.rid, true).subscribe(
      (res: any) => {
        this.response.request.viewData = res.request.viewData;
        this.response.questions = res.questions;
      },
      () => {
        this.redirect('information-requests');
      }
    );
  }

  showShare() {
    const baseUrl = this.hostnameService.getOrigin();
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    const link = `/request/${this.rid}`;
    const data = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid: this.rid,
        usersLackingAccessList: ''
      }
    };

    this.dialog.open(InviteShareComponent, {
      data,
      panelClass: 'show-invite'
    });
  }

  onMenuEvents(action) {
    const { key } = action;
    switch (key) {
      case 'submit':
        this.checkValidationAndSubmit('submitted');
        break;

      case 'reopen':
        if (action.label === 'Reject') {
          this.submitRejection('reopened');
        } else {
          this.submitRequestData('reopened');
        }
        break;

      case 'close':
        const { plugin_id } = this.response;
        if ([QuestionnaireTypes.COMPLIANCE_QUESTIONNAIRE].includes(plugin_id)) {
          this.submitPartialRequest();
        } else {
          this.submitRequestData('closed');
        }
        break;

      case 'share':
        this.showShare();
        break;

      case 'back':
        this.redirectToBack();
        break;

      case 'scip_notification':
        this.createScipNotification();
        break;

      default:
        // optionally handle unexpected keys
        break;
    }
  }

  submitRejection(status) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          type: 'createTemplate',
          header: 'Please explain the reason for rejection to your supplier',
          text: ' ',
          cancel_text: 'Cancel',
          publish_text: 'Confirm'
        }
      })
      .afterClosed()
      .subscribe((message) => {
        if (message) {
          this.submitRequestData(status, { rejection_reason: message });
        }
      });
  }

  submitRequestData(status, payload = {}) {
    // stop submitting request when all mandatory fields are not filled.
    if (Object.values(this.validCheck).some((v) => !v)) {
      this.toastrService.show('All mandatory fields are not filled.', 4000);
      return;
    }

    this.errorMessages = [];

    this.makersiteService
      .submitRequestData(this.rid, status, payload)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 422) {
            this.errorMessages = err.error.result.errors;

            this.openErrorDialog();
          }
          return throwError(() => err);
        })
      )
      .subscribe((res: any) => {
        this.toastrService.show(res.message, 2000);
        this.init();
      });
  }

  submitPartialRequest() {
    this.informationRequestService.submitStatusUpdates(this.rid).subscribe((res: any) => {
      this.toastrService.show(res.message, 3000);
      this.init();
    });
  }

  checkHasPartialDeclaration(): boolean {
    let foundPartial = false;
    this.gridApi.forEachNode((node) => {
      const supplier = node?.data;
      if (supplier?.lines?.length > 0) {
        const missingDeclaration = supplier.lines.some(
          (line) => line?.key.includes('out_l_declaration_') && line?.value === null
        );
        if (missingDeclaration) {
          foundPartial = true;
        }
      }
    });
    return foundPartial;
  }

  /**
   * Checks the validation status and submits the data if valid or confirmed.
   * @param status - The status to be submitted with the request.
   */
  checkValidationAndSubmit(status: any): void {
    const { plugin_id, tabs } = this.response;
    if ([QuestionnaireTypes.PCF_QUESTIONNAIRE, QuestionnaireTypes.EPD_QUESTIONNAIRE].includes(plugin_id)) {
      return;
    }
    const isCBAMQuestionnaire = [
      QuestionnaireTypes.CBAM_STEEL_QUESTIONNAIRE,
      QuestionnaireTypes.CBAM_ALUM_QUESTIONNAIRE
    ].includes(plugin_id);

    if (isCBAMQuestionnaire) {
      const invalidTabs = this.questionnairesValidator.checkValidity(tabs);

      if (invalidTabs.length > 0) {
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              type: 'Confirm',
              header: 'It looks like some information is missing. Would you like to submit your answer as is?',
              text: ' ',
              cancel_text: 'No',
              confirm_text: 'Yes'
            }
          })
          .afterClosed()
          .subscribe((confirm) => {
            if (confirm) {
              this.submitRequestData(status);
            }
          });
      } else {
        this.submitRequestData(status);
      }
    } else if (QuestionnaireTypes.COMPLIANCE_QUESTIONNAIRE === plugin_id) {
      const isPartialSubmission = this.checkHasPartialDeclaration();
      if (isPartialSubmission) {
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              type: 'Confirm',
              header: 'Submit Questionnaire',
              text: 'A new questionnaire will be created for the products for which no compliance statement was provided. Please make sure to provide the missing information before the due date',
              cancel_text: 'Cancel',
              confirm_text: 'Confirm'
            }
          })
          .afterClosed()
          .subscribe((confirm) => {
            if (confirm) {
              this.submitRequestData(status);
            }
          });
      } else {
        this.submitRequestData(status);
      }
    } else {
      this.submitRequestData(status);
    }
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  createScipNotification() {
    this.makersiteService.createScipNotification(this.rid).subscribe((res: any) => {
      if (res[0]?.details?.product_rid) {
        this.menuService.clearMenuConfig();
        this.toastrService.show('SCIP notification created successfully', 2000);
        this.router.navigate([`scip/${res[0]?.details?.product_rid.replace('#', '')}`], { replaceUrl: true });
      }
    });
  }

  redirectToBack() {
    if (this.isUserExternalSupplier) {
      this.router.navigate(['information-requests/supplier']);
    } else {
      if (this.response.info?.campaign?.rid) {
        this.router.navigate([`data-collection/campaign/${this.response.info?.campaign?.rid.replace('#', '')}`]);
      } else {
        this.router.navigate(['data-collection'], { queryParams: { request: true } });
      }
    }
  }

  openErrorDialog(): void {
    this.errorDialogRef = this.dialog.open(MKConfirmationDialogComponent, {
      data: {
        title: 'Missing data',
        titleIcon: MdsIcon.ERROR_CIRCLE,
        titleIconColor: ColorAliases.HIGHLIGHTS_ALERT,
        body: this.errorDialogBody,
        actions: this.errorDialogActions
      }
    });
  }
}
